import moment from "moment";
import { UserQuotaConfig } from "../model/BenefitClientServiceModel";
import { CancelTatUnit, TimeSlots } from "@thriwe-2-0/thriwe-ui-utils";

export class CommonUtils {
  // Define the static method correctly without using `const`

  static getNonBlankString(value?: string, fallbackValue?: string) {
    if (value && (value?.trim()?.length ?? 0) > 0) {
      return value;
    }
    return fallbackValue;
  }

  static currencyFormat(
    amount?: number,
    currency: string = "INR",
    options: Intl.NumberFormatOptions = {}
  ) {
    try {
      const formattedAmount = Number(amount) || 0;
      const validCurrency =
        currency && /^[A-Z]{3}$/.test(currency) ? currency : undefined;
      return formattedAmount.toLocaleString("en-US", {
        style: "currency",
        currency: validCurrency,
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
        ...options,
      });
    } catch (error) {
      return amount?.toString() ?? "0";
    }
  }

  static capitalizeFirstLetter = (text: string): string => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  static findHaveAvailableQuota(data: UserQuotaConfig[], configId: string) {
    const yearlyAvailableQuota = data?.filter(
      (quota: any) =>
        quota?.benefit_group_config_id?.toString() === configId?.toString() &&
        quota?.unit === `YEAR` &&
        quota?.total_quota > 0
    );

    if (yearlyAvailableQuota?.length > 0) {
      const quotaObject = data?.filter(
        (userQuota: UserQuotaConfig, index: any) =>
          userQuota?.month === moment().format("MMMM") &&
          userQuota?.benefit_group_config_id?.toString() ===
            configId?.toString()
      );

      if (quotaObject?.length === 0) {
        return false;
      } else if (quotaObject?.length > 1) {
        return false;
      } else if (quotaObject?.length === 1) {
        return quotaObject?.[0]?.quota_available > 0;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }
}

export default CommonUtils;

export function getPlatformAndBrowser() {
  const userAgent = navigator.userAgent;

  // Detect Platform OS
  let platform = "Unknown OS";
  if (/windows phone/i.test(userAgent)) {
    platform = "Windows Phone";
  } else if (/android/i.test(userAgent)) {
    platform = "Android";
  } else if (/iPad|iPhone|iPod/.test(userAgent)) {
    platform = "iOS";
  } else if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.platform)) {
    platform = "macOS";
  } else if (/Win32|Win64|Windows|WOW64/.test(navigator.platform)) {
    platform = "Windows";
  } else if (/Linux/.test(navigator.platform)) {
    platform = "Linux";
  }

  // Detect Browser
  let browser = "Unknown";
  if (userAgent.indexOf("Edg") > -1) {
    browser = "Microsoft Edge";
  } else if (userAgent.indexOf("Chrome") > -1) {
    browser = "Chrome";
  } else if (userAgent.indexOf("Firefox") > -1) {
    browser = "Firefox";
  } else if (
    userAgent.indexOf("Safari") > -1 &&
    userAgent.indexOf("Chrome") === -1
  ) {
    browser = "Safari";
  } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
    browser = "Opera";
  } else if (
    userAgent.indexOf("Trident") > -1 ||
    userAgent.indexOf("MSIE") > -1
  ) {
    browser = "Internet Explorer";
  }

  return `${browser} - ${platform}`;
}

export function transformSlotData(
  data: TimeSlots[]
): Record<string, null | { from: string; to: string }> {
  const daysOfWeek: string[] = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const transformedData: Record<string, null | { from: string; to: string }> =
    daysOfWeek.reduce((acc, day) => {
      acc[day] = null;
      return acc;
    }, {} as Record<string, null | { from: string; to: string }>);

  data.forEach(({ calenderDate, slots }) => {
    const dayOfWeek = moment(calenderDate).format("dddd");
    if (slots && slots.length > 0) {
      const { fromTime, toTime } = slots[0];
      transformedData[dayOfWeek] = {
        from: `${moment(fromTime, "HH:mm").format("h:mm A")}`,
        to: `${moment(toTime, "HH:mm").format("h:mm A")}`,
      };
    }
  });

  return transformedData;
}

export function formatToTwoDecimalPlaces(value: any): string {
  const num = Number(value);
  if (isNaN(num)) return "0";
  return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
}

export const formattedTatDuration = (
  tatValue: number,
  tatUnit: number
): string => {
  switch (tatUnit) {
    case CancelTatUnit.year:
      return `${tatValue} ${tatValue > 1 ? "years" : "year"}`;
    case CancelTatUnit.month:
      return `${tatValue} ${tatValue > 1 ? "months" : "month"}`;
    case CancelTatUnit.day:
      return `${tatValue} ${tatValue > 1 ? "days" : "day"}`;
    case CancelTatUnit.hour:
      return `${tatValue} ${tatValue > 1 ? "hours" : "hour"}`;
    case CancelTatUnit.minute:
      return `${tatValue} ${tatValue > 1 ? "minutes" : "minute"}`;
    case CancelTatUnit.second:
      return `${tatValue} ${tatValue > 1 ? "seconds" : "second"}`;
    case CancelTatUnit.clearDay:
      return `${tatValue} ${tatValue > 1 ? "clear days" : "clear day"}`;
    default:
      return `${tatValue} unit(s)`;
  }
};
